import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserPlus, Receipt, LogOut, Menu, X,
  Stethoscope, Settings, FlaskConical, CalendarDays, ActivitySquare,
  Pill, Ticket, DatabaseBackup, ChevronLeft, ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LiveClock from './LiveClock';
import ThemeToggle from './ThemeToggle';
import { CLINIC_NAME, CLINIC_SUBTITLE } from '../utils/clinicConfig';

export default function AppLayout({ children, title, subtitle }) {
  // ── Font size zoom (persists across sessions) ────────────────────────────
  const [zoomLevel, setZoomLevel] = React.useState(() => Number(localStorage.getItem('app_zoom') || 100));

  React.useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (zoomLevel === 100) {
      root.style.removeProperty('zoom');
      root.style.removeProperty('transformOrigin');
    } else {
      root.style.zoom = (zoomLevel / 100).toFixed(2);
    }
    localStorage.setItem('app_zoom', zoomLevel);
  }, [zoomLevel]);

  const zoomIn    = () => setZoomLevel(s => Math.min(s + 10, 150));
  const zoomOut   = () => setZoomLevel(s => Math.max(s - 10, 70));
  const zoomReset = () => setZoomLevel(100);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed ? 'true' : 'false');
  }, [collapsed]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.sidebar')) setMobileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileOpen]);

  function handleLogout() { logout(); navigate('/login'); }

  const doctorNav = [
    { to: '/doctor',               label: 'Dashboard',          icon: LayoutDashboard, end: true },
    { to: '/doctor/create-token',  label: 'Create Token',       icon: Ticket },
    { to: '/doctor/queue',         label: 'Patient Queue',      icon: Users },
    { to: '/doctor/patients',      label: 'All Patients',       icon: UserPlus },
    { to: '/doctor/tracker',       label: 'Patient Tracker',    icon: ActivitySquare },
    { to: '/doctor/billing',       label: 'Billing',            icon: Receipt },
    { to: '/doctor/lab',           label: 'LAB',                icon: FlaskConical },
    { to: '/doctor/pharmacy',      label: 'Pharmacy',           icon: Pill },
    { to: '/doctor/treatments',    label: 'Treatment Catalog',  icon: Stethoscope },
    { to: '/doctor/calendar',      label: 'Calendar',           icon: CalendarDays },
    { to: '/doctor/sterilisation', label: 'Sterilisation Log',  icon: ShieldCheck },
    { to: '/doctor/settings',      label: 'Account Settings',   icon: Settings },
    { to: '/doctor/backup',        label: 'Backup & Restore',   icon: DatabaseBackup },
  ];

  const receptionistNav = [
    { to: '/receptionist',               label: 'Create Token',       icon: UserPlus, end: true },
    { to: '/receptionist/queue',         label: "Today's Queue",      icon: Users },
    { to: '/receptionist/patients',      label: 'All Patients',       icon: LayoutDashboard },
    { to: '/receptionist/tracker',       label: 'Patient Tracker',    icon: ActivitySquare },
    { to: '/receptionist/billing',       label: 'Billing',            icon: Receipt },
    { to: '/receptionist/lab',           label: 'LAB',                icon: FlaskConical },
    { to: '/receptionist/pharmacy',      label: 'Pharmacy',           icon: Pill },
    { to: '/receptionist/treatments',    label: 'Treatment Catalog',  icon: Stethoscope },
    { to: '/receptionist/calendar',      label: 'Calendar',           icon: CalendarDays },
    { to: '/receptionist/sterilisation', label: 'Sterilisation Log',  icon: ShieldCheck },
    { to: '/receptionist/settings',      label: 'Account Settings',   icon: Settings },
  ];

  const navItems = user?.role === 'doctor' ? doctorNav : receptionistNav;

  return (
    <div className="app-shell">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1400 }} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''} ${collapsed ? 'collapsed' : ''}`}>

        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon"><Stethoscope size={20} /></div>
          {!collapsed && (
            <div>
              <div className="sidebar-brand-text">{CLINIC_NAME}</div>
              <div className="sidebar-brand-sub">{CLINIC_SUBTITLE}</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        {!collapsed && <div className="nav-section-label">Menu</div>}
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'nav-item-icon' : ''}`}
            title={collapsed ? item.label : undefined}
            onClick={() => setMobileOpen(false)}>
            <item.icon size={17} />
            {!collapsed && item.label}
          </NavLink>
        ))}

        {/* Footer */}
        <div className="sidebar-footer">
          {!collapsed && (
            <div className="user-chip">
              <div className="user-avatar">{(user?.displayName || user?.username || '?').charAt(0).toUpperCase()}</div>
              <div>
                <div className="user-info-name">{user?.displayName || user?.username}</div>
                <div className="user-info-role">{user?.role}</div>
              </div>
            </div>
          )}
          <button className={`nav-item ${collapsed ? 'nav-item-icon' : ''}`}
            style={{ marginTop:8, color:'var(--color-danger)' }}
            title={collapsed ? 'Log out' : undefined}
            onClick={handleLogout}>
            <LogOut size={17} />
            {!collapsed && 'Log out'}
          </button>
        </div>

        {/* Zoom controls in sidebar — accessible on mobile */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'8px 0', borderTop:'1px solid var(--color-border)', marginTop:8 }}>
          <button type="button" onClick={zoomOut} disabled={zoomLevel <= 70}
            style={{ background:'var(--color-surface-raised)', border:'1px solid var(--color-border)', borderRadius:6, cursor:'pointer', padding:'4px 10px', fontSize:13, fontWeight:800, color:'var(--color-ink-soft)' }}>A−</button>
          <span style={{ fontSize:11, color:'var(--color-ink-faint)', minWidth:36, textAlign:'center', cursor:'pointer' }} onClick={zoomReset}>{zoomLevel}%</span>
          <button type="button" onClick={zoomIn} disabled={zoomLevel >= 150}
            style={{ background:'var(--color-surface-raised)', border:'1px solid var(--color-border)', borderRadius:6, cursor:'pointer', padding:'4px 10px', fontSize:15, fontWeight:800, color:'var(--color-ink-soft)' }}>A+</button>
        </div>

        {/* Desktop collapse toggle — pinned to bottom of sidebar */}
        <button
          className="sidebar-collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          {!collapsed && <span style={{ fontSize:12 }}>Minimise</span>}
        </button>
      </aside>

      {/* Main */}
      <main className="main-content">
        <div className="topbar no-print">
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* Mobile menu button — shown only on small screens via CSS */}
            <button className="btn btn-icon btn-outline mobile-menu-btn"
              onClick={() => setMobileOpen(o => !o)}>
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <div className="page-title topbar-title">{title}</div>
              {subtitle && <div className="page-subtitle">{subtitle}</div>}
            </div>
          </div>
          <div className="topbar-right">
            <LiveClock />
            {/* Zoom controls — visible on desktop, hidden on mobile (in sidebar instead) */}
            <div className="zoom-controls no-print" style={{ display:'flex', alignItems:'center', gap:2, border:'1px solid var(--color-border)', borderRadius:8, padding:'2px 4px' }} title="Page zoom">
              <button type="button" onClick={zoomOut} disabled={zoomLevel <= 70}
                style={{ background:'none', border:'none', cursor:'pointer', padding:'3px 8px', fontSize:13, fontWeight:800, color:'var(--color-ink-soft)', lineHeight:1 }}
                title="Zoom out">A−</button>
              <span className="zoom-pct" style={{ fontSize:11, color:'var(--color-ink-faint)', minWidth:32, textAlign:'center', userSelect:'none', cursor:'pointer' }}
                onClick={zoomReset} title="Reset zoom">{zoomLevel}%</span>
              <button type="button" onClick={zoomIn} disabled={zoomLevel >= 150}
                style={{ background:'none', border:'none', cursor:'pointer', padding:'3px 8px', fontSize:15, fontWeight:800, color:'var(--color-ink-soft)', lineHeight:1 }}
                title="Zoom in">A+</button>
            </div>
            <ThemeToggle />
          </div>
        </div>
        {children}
      </main>

      <style>{`
        @media (max-width: 860px) {
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
